import { morph } from '@theme/morph';

const MAX_SECTION_CACHE_ENTRIES = 24;

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The abort controllers by fetch URL
   * @type {Map<string, AbortController>}
   */
  #fetchAbortControllers = new Map();

  /**
   * The pending promises
   * @type {Map<string, { promise: Promise<string>, controller: AbortController }>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {string[]} [options.targets] - Partial morph selectors scoped to the section response
   * @param {boolean} [options.replace] - Replace the complete section instead of preserving existing nodes
   * @param {URL} [options.url] - URL to render the section against
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, targets, replace = false, url = new URL(window.location.href) } = options ?? {};

    this.#abortPendingMorph(sectionId);
    this.#abortPendingFetch(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url, {
      signal: abortController.signal,
    });

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);

      if (replace) {
        replaceSection(sectionId, sectionHTML);
      } else if (targets?.length) {
        morphSectionTargets(sectionId, sectionHTML, targets);
      } else {
        morphSection(sectionId, sectionHTML);
      }

      return sectionHTML;
    }

    if (replace || targets?.length) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * @param {string} sectionId
   */
  #abortPendingFetch(sectionId) {
    const normalizedSectionId = normalizeSectionId(sectionId);
    const fetchAbort = this.#fetchAbortControllers.get(normalizedSectionId);
    fetchAbort?.abort();
  }

  /**
   * @param {string} url
   * @param {string} html
   */
  #setCacheEntry(url, html) {
    if (this.#cache.has(url)) {
      this.#cache.delete(url);
    }

    this.#cache.set(url, html);

    while (this.#cache.size > MAX_SECTION_CACHE_ENTRIES) {
      const oldestKey = this.#cache.keys().next().value;
      if (oldestKey === undefined) break;
      this.#cache.delete(oldestKey);
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @param {Object} [options]
   * @param {AbortSignal} [options.signal]
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href), options = {}) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);
    const normalizedSectionId = normalizeSectionId(sectionId);

    const pendingEntry = this.#pendingPromises.get(sectionUrl);
    if (pendingEntry && !pendingEntry.controller.signal.aborted) return pendingEntry.promise;
    if (pendingEntry?.controller.signal.aborted) this.#pendingPromises.delete(sectionUrl);

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }

    const previousFetchAbort = this.#fetchAbortControllers.get(normalizedSectionId);
    previousFetchAbort?.abort();

    const fetchAbortController = new AbortController();
    this.#fetchAbortControllers.set(normalizedSectionId, fetchAbortController);

    if (options.signal) {
      if (options.signal.aborted) {
        fetchAbortController.abort();
      } else {
        options.signal.addEventListener(
          'abort',
          () => {
            fetchAbortController.abort();
          },
          { once: true }
        );
      }
    }

    const pendingPromise = fetch(sectionUrl, { signal: fetchAbortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to render section ${sectionId}`);
        }
        return response.text();
      })
      .finally(() => {
        const currentEntry = this.#pendingPromises.get(sectionUrl);
        if (currentEntry?.promise === pendingPromise) {
          this.#pendingPromises.delete(sectionUrl);
        }
        if (this.#fetchAbortControllers.get(normalizedSectionId) === fetchAbortController) {
          this.#fetchAbortControllers.delete(normalizedSectionId);
        }
      });

    this.#pendingPromises.set(sectionUrl, { promise: pendingPromise, controller: fetchAbortController });

    try {
      const sectionHTML = await pendingPromise;

      if (useCache && !fetchAbortController.signal.aborted) {
        this.#setCacheEntry(sectionUrl, sectionHTML);
      }

      return sectionHTML;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      throw error;
    }
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) continue;
      if (containsShadowRoot(section)) continue;

      this.#setCacheEntry(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
export function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${normalizeSectionId(sectionId)}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

function injectSectionStylesheets(parsedDocument, sectionElement) {
  if (!parsedDocument || !sectionElement) return;

  const newStylesheet = parsedDocument.querySelector('style[data-section-stylesheet]');
  if (newStylesheet) {
    const existingStylesheet =
      sectionElement.querySelector(':scope > style[data-section-stylesheet]') ||
      sectionElement.querySelector('style[data-section-stylesheet]');
    if (existingStylesheet) {
      if (existingStylesheet.textContent !== newStylesheet.textContent) {
        existingStylesheet.textContent = newStylesheet.textContent;
      }
    } else {
      const style = document.createElement('style');
      for (const attr of newStylesheet.attributes) {
        style.setAttribute(attr.name, attr.value);
      }
      style.textContent = newStylesheet.textContent;
      sectionElement.prepend(style);
    }
  }

  sectionElement.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href || link.sheet) return;
    const fresh = document.createElement('link');
    fresh.rel = 'stylesheet';
    fresh.href = href;
    const media = link.getAttribute('media');
    if (media) fresh.media = media;
    const type = link.getAttribute('type');
    if (type) fresh.type = type;
    link.replaceWith(fresh);
  });

  parsedDocument.querySelectorAll('link[rel="stylesheet"]').forEach((parsedLink) => {
    const href = parsedLink.getAttribute('href');
    if (!href) return;
    const applied = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some((link) => {
      return link.getAttribute('href') === href && (Boolean(link.sheet) || link.isConnected);
    });
    if (applied) return;
    const live = document.createElement('link');
    live.rel = 'stylesheet';
    live.href = href;
    const media = parsedLink.getAttribute('media');
    if (media) live.media = media;
    const type = parsedLink.getAttribute('type');
    if (type) live.type = type;
    document.head.appendChild(live);
  });
}

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    console.error(`Section ${sectionId} not found. Looking for: ${buildSectionSelector(sectionId)}`);
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    console.error(`Section ${sectionId} not found in the section rendering response. HTML length: ${html.length}`);
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement);
  injectSectionStylesheets(fragment, existingElement);
  finalizeSectionMorph(sectionId, existingElement);
}

/**
 * Replaces a complete section with one authoritative Section Rendering response.
 * This intentionally discards stale form values, pagination state, and event listeners.
 *
 * @param {string} sectionId
 * @param {string} html
 */
export function replaceSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const selectorId = buildSectionSelector(sectionId);
  const existingElement = document.getElementById(selectorId);
  const newElement = fragment.getElementById(selectorId);

  if (!existingElement || !newElement) {
    throw new Error(`Section ${sectionId} not found while replacing filtered results`);
  }

  existingElement.replaceWith(newElement);
  injectSectionStylesheets(fragment, newElement);
  finalizeSectionMorph(sectionId, newElement);
}

/**
 * @param {string} sectionId
 * @param {string} html
 * @param {string[]} targets
 */
export function morphSectionTargets(sectionId, html, targets) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const sectionRoot =
    fragment.getElementById(buildSectionSelector(sectionId)) ||
    fragment.querySelector('.shopify-section');

  if (!sectionRoot) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  /** @type {Set<Element>} */
  const morphedRoots = new Set();

  for (const selector of targets) {
    const existingElement = document.querySelector(selector);
    const newElement = sectionRoot.querySelector(selector);

    if (!existingElement || !newElement) continue;

    morph(existingElement, newElement);
    morphedRoots.add(existingElement);
  }

  if (morphedRoots.size === 0) {
    morphSection(sectionId, html);
    return;
  }

  const sectionElement = document.getElementById(buildSectionSelector(sectionId));
  if (sectionElement) injectSectionStylesheets(fragment, sectionElement);
  finalizeSectionMorph(sectionId, sectionElement, morphedRoots);
}

/**
 * @param {string} sectionId
 * @param {HTMLElement | null} scope
 * @param {Set<Element>} [morphedRoots]
 */
function finalizeSectionMorph(sectionId, scope, morphedRoots) {
  requestAnimationFrame(() => {
    const roots = morphedRoots && morphedRoots.size > 0 ? Array.from(morphedRoots) : scope ? [scope] : [];

    roots.forEach((root) => {
      root.querySelectorAll('facets-form-component').forEach((form) => {
        if (!form.isConnected) return;

        if (form.disconnectedCallback) {
          form.disconnectedCallback();
        }
        if (form.connectedCallback) {
          form.connectedCallback();
        }
      });
    });

    document.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId, partial: Boolean(morphedRoots?.size) },
      })
    );
  });
}

export function getCollectionFilterMorphTargets(sectionId) {
  return [
    `#shopify-section-${sectionId} .collection-wrapper`,
    `#MobileActiveFilters--${sectionId}`,
    '#filters-drawer',
  ];
}

export const sectionRenderer = new SectionRenderer();
